<template>
  <div>
    <v-row class="my-3">
      <v-icon class="mr-2" color="primary">question_answer</v-icon>
      <div class="subtitle-1">{{ $t("tickets.view.comments") }}</div>
    </v-row>

    <!-- New comment -->
    <div class="ml-5" v-if="!isReadOnlyUser">
      <div class="mb-1">
        <v-icon class="mr-2 mb-1" color="primary">person</v-icon>
        {{ username }}
      </div>

      <v-form ref="form" v-model="valid" lazy-validation>
        <v-textarea
          class="mb-3"
          data-cy="content"
          v-model="comment.content"
          dense
          hide-details
          outlined
          :rules="requiredRule"
          rows="1"
          auto-grow
        />

        <v-row class="mb-3 mt-1">
          <div class="flex-grow-1"></div>
          <v-btn
            class="mr-3"
            color="primary"
            data-cy="send"
            @click="postMessage(item)"
          >
            senden
          </v-btn>
        </v-row>
      </v-form>
    </div>

    <!-- Existing comments -->
    <div class="ml-5" v-for="c in comments" :key="c._id">
      <div class="mb-1 d-flex align-center">
        <div>
          <v-icon class="mr-2 mb-1" color="primary">person</v-icon>
          {{ c.user?.name || "-" }} - <i>{{ formatDate(c.timestamp) + "h" }}</i>
        </div>

        <div class="flex-grow-1"></div>

        <!-- Actions -->
        <template v-if="canModify(c)">
          <template v-if="editingId !== c._id">
            <v-btn icon @click="startEdit(c)" :aria-label="$t('general.edit')">
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn
              icon
              @click="deleteOne(item, c)"
              :aria-label="$t('general.delete')"
            >
              <v-icon color="error">delete</v-icon>
            </v-btn>
          </template>

          <template v-else>
            <v-btn
              small
              color="primary"
              class="mr-2"
              @click="saveEdit(item, c)"
            >
              {{ $t("general.save") }}
            </v-btn>
            <v-btn small text @click="cancelEdit">
              {{ $t("general.cancel") }}
            </v-btn>
          </template>
        </template>
      </div>

      <!-- Readonly view -->
      <v-textarea
        v-if="editingId !== c._id"
        class="mb-4"
        :value="c.content"
        dense
        hide-details
        outlined
        disabled
        auto-grow
        background-color="grey lighten-3"
        :rows="(c.content || '').split('\n').length"
      />

      <!-- Edit mode -->
      <v-textarea
        v-else
        class="mb-2"
        v-model="editDraft"
        dense
        hide-details
        outlined
        auto-grow
        :rules="requiredRule"
        rows="1"
      />

      <!-- optional: small error hint per comment edit -->
      <div
        v-if="editingId === c._id && editError"
        class="error--text text-caption mb-3"
      >
        {{ editError }}
      </div>
    </div>

    <!-- Optional delete confirm dialog -->
    <v-dialog v-model="deleteDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t("comments.deleteTitle") }}
        </v-card-title>
        <v-card-text> {{ $t("comments.deleteText") }} </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn text @click="closeDeleteDialog">{{
            $t("general.cancel")
          }}</v-btn>
          <v-btn color="error" @click="confirmDelete">{{
            $t("general.delete")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { dateUtil } from "../../../../components/api/api";

export default {
  props: ["item"],
  data() {
    return {
      valid: false,
      requiredRule: [(v) => !!v || this.$t("general.fieldNeedsToBeFilled")],

      comment: {
        content: "",
        user: null,
        timestamp: null,
      },

      comments: [],
      username: "",

      // current user id for permissions
      currentUserId: null,

      // edit state
      editingId: null,
      editDraft: "",
      editError: "",

      // delete confirm state
      deleteDialog: false,
      deleteTarget: null, // { ticketId, commentId }
    };
  },
  watch: {
    // Reload when ticket changes
    item: {
      handler() {
        this.loadMessanges();
      },
      immediate: true,
    },
  },
  methods: {
    async loadMessanges() {
      // Trigger user info
      await this.$store.dispatch("getUserInfo").then((r) => {
        this.username = r?.name || "";
      });

      // Cache current user id from localStorage (used by canModify)
      try {
        this.currentUserId =
          JSON.parse(localStorage.getItem("userData"))?._id || null;
      } catch (e) {
        this.currentUserId = null;
      }

      // Load comments
      await this.$store
        .dispatch("getCommentsForTicket", this.item._id)
        .then((r) => {
          this.comments = Array.isArray(r) ? r : [];
        });

      // Clear "new comment" form + cancel edits
      this.clearComment();
      this.cancelEdit();
    },

    // ------- Create comment -------
    postMessage(item) {
      if (this.isReadOnlyUser) return;
      if (!this.$refs.form || !this.$refs.form.validate()) return;

      // copy to avoid mutating this.comment object while trimming
      const postData = {
        content: (this.comment.content || "").toString(),
        user: null,
        timestamp: null,
      };

      while (postData.content.endsWith("\n")) {
        postData.content = postData.content.substring(
          0,
          postData.content.length - 1
        );
      }

      postData.user = this.currentUserId;
      postData.timestamp = new Date();
      postData.id = item._id;

      this.$store.dispatch("addComment", postData).then(() => {
        this.clearComment();
        this.loadMessanges();
      });
    },

    clearComment() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.comment = { content: "", user: null, timestamp: null };
    },

    // ------- Edit comment -------
    startEdit(c) {
      this.editError = "";
      this.editingId = c._id;
      this.editDraft = c.content || "";
    },

    cancelEdit() {
      this.editingId = null;
      this.editDraft = "";
      this.editError = "";
    },

    saveEdit(ticket, c) {
      if (this.isReadOnlyUser) return;
      this.editError = "";

      let content = (this.editDraft || "").toString();
      while (content.endsWith("\n")) content = content.slice(0, -1);

      if (!content.trim()) {
        this.editError = this.$t("general.fieldNeedsToBeFilled");
        return;
      }

      console.log(c, "test");

      // Requires Vuex action: updateComment({ ticketId, commentId, content })
      this.$store
        .dispatch("updateComment", {
          ticketId: ticket._id,
          commentId: c._id,
          content,
        })
        .then(() => {
          this.cancelEdit();
          this.loadMessanges();
        });
    },

    // ------- Delete comment -------
    deleteOne(ticket, c) {
      // If you don’t want a confirm dialog, replace this with direct dispatch.
      this.deleteTarget = { ticketId: ticket._id, commentId: c._id };
      this.deleteDialog = true;
    },

    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteTarget = null;
    },

    confirmDelete() {
      if (this.isReadOnlyUser) return;
      if (!this.deleteTarget) return;

      // Requires Vuex action: deleteComment({ ticketId, commentId })
      this.$store
        .dispatch("deleteComment", this.deleteTarget)
        .then(() => {
          this.closeDeleteDialog();
          this.loadMessanges();
        })
        .catch(() => {
          this.closeDeleteDialog();
        });
    },

    // ------- Permissions helper -------
    canModify(c) {
      // Allow edit/delete if author is current user.
      // Adjust if you also want admins/managers.
      const commentUserId = (c.user && (c.user._id || c.user)) || null; // populated user OR raw ObjectId
      return (
        !this.isReadOnlyUser &&
        !!this.currentUserId &&
        !!commentUserId &&
        String(commentUserId) === String(this.currentUserId)
      );
    },

    // ------- Utils -------
    formatDate(date) {
      if (!date) return null;
      return dateUtil(new Date(date), true, "FEHLER");
    },
  },
};
</script>
